import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { DEV_MODE } from "Constant/app/process";
import { ClipLoader } from "react-spinners";

import { getLoginButtonComponentWithCode } from "./LoginButton.helpers";
import { ExtractedParams, LoginButtonProps } from "./LoginButton.types";

/**
 * Generic OAuth Login Button Component
 *
 * Handles OAuth popup flow for various authentication providers.
 * Supports both direct URL and lazy URL fetching patterns.
 * Supports extracting any OAuth callback parameters (code, token, state, etc.).
 *
 * Two acquisition paths run in parallel: polling the popup URL and a
 * window "message" listener (postMessage from the redirect page). Both
 * funnel into the same extractedParams state.
 *
 * @example
 * // Simple usage with 'code' parameter
 * <LoginButton
 *   url={authUrl}
 *   paramNames={["code"]}
 *   onSuccess={(data) => handleAuth(data)}
 *   logoSrc="/assets/images/logo.png"
 *   buttonText="Connect with Provider"
 * />
 *
 * @example
 * // With data transformation
 * <LoginButton
 *   getAuthUrl={fetchAuthUrl}
 *   url={authUrl}
 *   paramNames={["code"]}
 *   onSuccess={onLoginSuccess}
 *   dataTransform={(params) => ({ ...params, source_type: 'hub' })}
 *   logoSrc="/assets/images/hubspot.png"
 *   buttonText="Connect with HubSpot"
 *   onLoadingChange={setIsButtonLoader}
 * />
 *
 * @example
 * // With custom styling
 * <LoginButton
 *   getAuthUrl={fetchDynamicsData}
 *   url={dynamicsURL}
 *   paramNames={["code"]}
 *   onSuccess={sendDataDynamics}
 *   logoSrc="/assets/images/dynamics-logo.png"
 *   logoWidth={40}
 *   buttonText="Microsoft Dynamics 365 Login"
 *   className="dnamicsBtn"
 *   isLoadingExternal={isLoadingButton}
 * />
 *
 * @example
 * // With multiple parameters (token-based auth)
 * <LoginButton
 *   url={authUrl}
 *   paramNames={["access_token", "state", "expires_in"]}
 *   onSuccess={(params) => handleTokenAuth(params)}
 *   dataTransform={(params) => ({
 *     token: params.access_token,
 *     state: params.state,
 *     expiresIn: params.expires_in,
 *   })}
 *   logoSrc="/assets/images/oauth-provider.png"
 *   buttonText="Connect with OAuth Provider"
 * />
 */
const LoginButton = React.forwardRef<any, LoginButtonProps>(
    (
        {
            url,
            getAuthUrl,
            getAuthUrlData,
            getAuthUrlResponseTransform = (response: any) => response,
            onAuthUrlResponse,
            onAuthUrlError,
            code,
            paramNames,
            requireAllParams = false,
            onSuccess,
            devSuccessParams,
            onError,
            dataTransform = (params: ExtractedParams) => params,
            logoSrc,
            buttonText,
            className = "",
            logoWidth = 30,
            logoStyle = {},
            disabled = false,
            isLoadingExternal = false,
            onLoadingChange,
            onPopupClose,
            popupDimensions = { width: 600, height: 600, left: 200, top: 200 },
        },
        ref,
    ) => {
        const [extractedParams, setExtractedParams] =
            useState<ExtractedParams | null>(null);
        const [isLoadingInternal, setIsLoadingInternal] =
            useState<boolean>(false);
        const newWindow = useRef<Window | null>(null);
        const intReference = useRef<ReturnType<typeof setInterval> | null>(
            null,
        );
        const isSuccessTrigger = useRef<boolean>(false);

        const isLoading = isLoadingInternal || isLoadingExternal;

        useEffect(() => {
            if (onLoadingChange) {
                onLoadingChange(isLoadingInternal);
            }
        }, [isLoadingInternal, onLoadingChange]);

        const resetLoading = useCallback(() => {
            setIsLoadingInternal(false);
            onLoadingChange?.(false);
        }, [onLoadingChange]);

        const openAuthWindow = useCallback(async () => {
            isSuccessTrigger.current = false;
            setExtractedParams(null);

            if (DEV_MODE && devSuccessParams) {
                setIsLoadingInternal(true);
                setExtractedParams(devSuccessParams);
                return;
            }

            const openPopupWithUrl = (authUrl: string) => {
                const { width, height, left, top } = popupDimensions;
                const windowFeatures = `width=${width},height=${height},left=${left},top=${top}`;
                const popup = window.open(authUrl, "_blank", windowFeatures);
                newWindow.current = popup;
                setIsLoadingInternal(true);
            };

            if (url) {
                openPopupWithUrl(url);
                return;
            }

            if (!getAuthUrl) return;

            const result = getAuthUrl(getAuthUrlData ?? undefined);
            setIsLoadingInternal(true);
            if (result == null) return;

            try {
                const response = await result;
                // Request helpers resolve with { status: false } on failure
                // instead of throwing, so the URL itself is the source of truth.
                const isSuccessfulResponse =
                    response && response?.status !== false;

                if (isSuccessfulResponse) {
                    onAuthUrlResponse?.(response);
                }

                const authUrl = isSuccessfulResponse
                    ? getAuthUrlResponseTransform(response)
                    : null;

                if (typeof authUrl === "string" && authUrl.trim() !== "") {
                    openPopupWithUrl(authUrl);
                } else {
                    resetLoading();
                    onAuthUrlError?.();
                }
            } catch {
                resetLoading();
                onAuthUrlError?.();
            }
        }, [
            url,
            getAuthUrl,
            getAuthUrlData,
            getAuthUrlResponseTransform,
            onAuthUrlResponse,
            onAuthUrlError,
            popupDimensions,
            resetLoading,
            devSuccessParams,
        ]);

        useEffect(() => {
            if (url && getAuthUrl && !newWindow.current) {
                openAuthWindow();
            }
        }, [url, getAuthUrl, openAuthWindow]);

        useEffect(() => {
            intReference.current = setInterval(() => {
                if (!newWindow.current || extractedParams) return;

                try {
                    const search = newWindow.current.location.search;
                    if (!search) return;

                    const urlParams = new URLSearchParams(search);
                    const error = urlParams.get("error");

                    if (error) {
                        newWindow.current?.close();
                        newWindow.current = null;
                        if (intReference.current) {
                            clearInterval(intReference.current);
                        }
                        setIsLoadingInternal(false);
                        onLoadingChange?.(false);
                        onError?.(error);
                        return;
                    }
                    const params: ExtractedParams = {};
                    let hasAtLeastOneParam = false;

                    paramNames.forEach((paramName) => {
                        const value = urlParams.get(paramName);
                        if (value !== null) {
                            params[paramName] = value;
                            hasAtLeastOneParam = true;
                        }
                    });

                    const hasRequiredParams = requireAllParams
                        ? paramNames.every(
                              (paramName) => params[paramName] !== undefined,
                          )
                        : hasAtLeastOneParam;

                    if (hasRequiredParams) {
                        setExtractedParams(params);
                    }
                } catch {}
            }, 300);

            return () => {
                if (intReference.current) {
                    clearInterval(intReference.current);
                }
            };
        }, [
            extractedParams,
            paramNames,
            requireAllParams,
            onError,
            onLoadingChange,
        ]);

        useEffect(() => {
            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                if (event.data?.type !== "oauth-callback") return;
                const incoming = (event.data.params ?? {}) as Record<
                    string,
                    string
                >;
                if (incoming.error) {
                    onError?.(incoming.error);
                    newWindow.current = null;
                    setIsLoadingInternal(false);
                    onLoadingChange?.(false);
                    return;
                }
                const params: ExtractedParams = {};
                let hasAtLeastOneParam = false;
                paramNames.forEach((paramName) => {
                    const value = incoming[paramName];
                    // Do we need to check if value is undefined or null?
                    params[paramName] = value;
                    hasAtLeastOneParam = true;
                });
                const hasRequiredParams = requireAllParams
                    ? paramNames.every(
                          (paramName) => params[paramName] !== undefined,
                      )
                    : hasAtLeastOneParam;
                if (hasRequiredParams) {
                    setExtractedParams(params);
                }
            };

            window.addEventListener("message", handleMessage);
            return () => window.removeEventListener("message", handleMessage);
        }, [paramNames, requireAllParams, onError, onLoadingChange]);

        useEffect(() => {
            const interval = setInterval(() => {
                if (newWindow.current && newWindow.current.closed) {
                    clearInterval(interval);
                    newWindow.current = null;
                    setIsLoadingInternal(false);
                    onLoadingChange?.(false);
                    onPopupClose?.();
                }
            }, 300);

            return () => clearInterval(interval);
        }, [isLoadingInternal, onLoadingChange, onPopupClose]);

        useEffect(() => {
            if (extractedParams && intReference.current) {
                clearInterval(intReference.current);
                newWindow.current?.close();
                newWindow.current = null;

                const data = dataTransform(extractedParams);

                if (onSuccess && !isSuccessTrigger.current) {
                    isSuccessTrigger.current = true;
                    onSuccess(data);
                }

                setIsLoadingInternal(false);
            }
        }, [extractedParams, dataTransform, onSuccess]);

        const computedLogoStyle: React.CSSProperties = {
            display: "inline-block",
            marginRight: "8px",
            ...logoStyle,
        };

        const LoginButtonComponent = useMemo(
            () => getLoginButtonComponentWithCode(code ?? undefined),
            [code],
        );

        return (
            <div className="position-relative overflow-hidden z-1">
                {isLoading && (
                    <div className="circle-loader">
                        <ClipLoader color={"#000"} size={17} />
                    </div>
                )}

                <LoginButtonComponent
                    ref={ref}
                    className={className}
                    disabled={disabled}
                    onClick={openAuthWindow}
                >
                    {logoSrc && (
                        <img
                            className="px-1"
                            style={computedLogoStyle}
                            src={logoSrc}
                            width={logoWidth}
                            alt=""
                        />
                    )}

                    <span>{buttonText}</span>
                </LoginButtonComponent>
            </div>
        );
    },
);

export default LoginButton;
